import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import { Bell, Lock, AlertCircle, Save, Loader2 } from 'lucide-react';

const ClientSettings = () => {
  const { user } = useSupabaseAuth();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [notifications, setNotifications] = useState({
    emailNewApplications: true,
    emailStatusUpdates: true,
    emailNewMessages: true,
    emailWeeklySummary: false,
  });

  const handleNotificationChange = (key) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
    setSuccess(false);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // In a real implementation, this would save to the database
      // For now, we'll just simulate a save
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Settings</h2>
        <p className="text-muted-foreground mt-1">Manage your account settings and preferences</p>
      </div>

      {success && (
        <Alert className="bg-green-50 text-green-900 border-green-200">
          <AlertDescription>Settings saved successfully!</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Account Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            <CardTitle>Account Information</CardTitle>
          </div>
          <CardDescription>Your account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Email Address</Label>
            <p className="text-base mt-1">{user?.email || 'Not available'}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Account Type</Label>
            <p className="text-base mt-1">Client</p>
          </div>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              To change your email address or password, please contact support.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <CardTitle>Notification Preferences</CardTitle>
          </div>
          <CardDescription>Manage how you receive updates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email-applications">New Applications</Label>
              <p className="text-sm text-muted-foreground">
                Receive email when candidates apply to your RFQs
              </p>
            </div>
            <Switch
              id="email-applications"
              checked={notifications.emailNewApplications}
              onCheckedChange={() => handleNotificationChange('emailNewApplications')}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email-updates">Status Updates</Label>
              <p className="text-sm text-muted-foreground">
                Get notified when application statuses change
              </p>
            </div>
            <Switch
              id="email-updates"
              checked={notifications.emailStatusUpdates}
              onCheckedChange={() => handleNotificationChange('emailStatusUpdates')}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email-messages">New Messages</Label>
              <p className="text-sm text-muted-foreground">
                Receive email when you get new messages from recruiters
              </p>
            </div>
            <Switch
              id="email-messages"
              checked={notifications.emailNewMessages}
              onCheckedChange={() => handleNotificationChange('emailNewMessages')}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email-weekly">Weekly Summary</Label>
              <p className="text-sm text-muted-foreground">
                Receive a weekly summary of all activity
              </p>
            </div>
            <Switch
              id="email-weekly"
              checked={notifications.emailWeeklySummary}
              onCheckedChange={() => handleNotificationChange('emailWeeklySummary')}
            />
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSaveSettings} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Preferences
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientSettings;
